import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {FormValue, mapParseItem, mapParseList, TranslateService} from '@brunata-angular/util';
import {SortingType} from '@brunata-angular/ui';
import {BehaviorSubject, filter, map, Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {
    ConsumptionAgent,
    ConsumptionAgentExport,
    ConsumptionAgentRequest,
    consumptionAgentSchema,
    ConsumptionAgentTranslated,
    convertableUnit,
    ConvertableUnit,
    setupConsumptionAgentRequestResultBody,
} from './consumption-agents-overview/consumption-agent.model';
import {
    MonitoringMeterBase,
    monitoringMeterBaseSchema,
} from 'src/app/monitoring/monitoring-meter.model';
import {
    CONSUMPTION_AGENT_RESULT_AGENT_TYPE_ENDPOINT,
    CONSUMPTION_AGENT_RESULT_MONITOR_ENDPOINT,
    ConsumptionAgentResult,
    consumptionAgentResultSchema,
} from './consumption-agent-result.model';
import {DateTime} from 'luxon';
import {CONSUMPTION_AGENT_TYPE_INVESTIGATIONS} from './create-consumption-agent/consumption-agent-investigation/consumption-agent-investigation.model';
import {ConsumptionAgentOverviewTableFilterFormGroup} from './consumption-agents-overview/consumption-agent-table/consumption-agent-overview.model';

@Injectable({
    providedIn: 'root',
})
export class ConsumptionAgentService {
    private readonly baseUrl = `${environment.onlineServiceUrl}/buildings`;
    private readonly http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);

    readonly sorting = new BehaviorSubject<
        SortingType<FormValue<ConsumptionAgentOverviewTableFilterFormGroup>>
    >({
        column: 'agentName',
        direction: 'asc',
    });
    readonly page = new BehaviorSubject<number>(1);
    readonly pageSize = new BehaviorSubject<number>(20);

    filterSubject =
        new BehaviorSubject<FormValue<ConsumptionAgentOverviewTableFilterFormGroup> | null>(null);

    updateSorting(
        sorting: SortingType<FormValue<ConsumptionAgentOverviewTableFilterFormGroup>>,
    ): void {
        this.sorting.next(sorting);
    }

    setFilterColumns(
        filterFormValues: FormValue<ConsumptionAgentOverviewTableFilterFormGroup>,
    ): void {
        this.filterSubject.next(filterFormValues);
    }

    getConsumptionAgents(buildingNo: string): Observable<ConsumptionAgent[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/consumptionagents`, {
                withCredentials: true,
            })
            .pipe(mapParseList(consumptionAgentSchema));
    }

    getMetersForAgent(buildingNo: string, reportId?: string): Observable<MonitoringMeterBase[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/metersforagent`, {
                withCredentials: true,
                params: {
                    ...(reportId !== undefined && {reportid: reportId}),
                },
            })
            .pipe(mapParseList(monitoringMeterBaseSchema));
    }

    getConvertableUnits(): Observable<ConvertableUnit[]> {
        return this.http
            .get(`${environment.onlineServiceUrl}/convertableunits`, {
                withCredentials: true,
            })
            .pipe(mapParseList(convertableUnit));
    }

    createAgent(
        buildingNo: string,
        agentRequest: ConsumptionAgentRequest,
    ): Observable<ConsumptionAgent> {
        return this.http
            .post(`${this.baseUrl}/${buildingNo}/consumptionagents`, agentRequest, {
                withCredentials: true,
            })
            .pipe(mapParseItem(consumptionAgentSchema));
    }

    updateAgent(
        buildingNo: string,
        reportId: string,
        agentRequest: ConsumptionAgentRequest,
    ): Observable<ConsumptionAgent> {
        return this.http
            .put(`${this.baseUrl}/${buildingNo}/consumptionagents/${reportId}`, agentRequest, {
                withCredentials: true,
            })
            .pipe(mapParseItem(consumptionAgentSchema));
    }

    deleteConsumptionAgent(buildingNo: string, reportId: string): Observable<unknown> {
        const url = `${this.baseUrl}/${buildingNo}/consumptionagents/${reportId}`;
        return this.http.delete(url, {withCredentials: true});
    }

    getConsumptionAgent(buildingNo: string, reportId: string): Observable<ConsumptionAgent> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/consumptionagents/${reportId}`, {
                withCredentials: true,
            })
            .pipe(mapParseItem(consumptionAgentSchema));
    }

    getConsumptionAgentResults(
        buildingNo: string,
        consumptionAgentTranslated: ConsumptionAgentTranslated,
    ): Observable<ConsumptionAgentResult> {
        const consumptionToMonitorEndpoint =
            CONSUMPTION_AGENT_RESULT_MONITOR_ENDPOINT[
                consumptionAgentTranslated.consumptionToMonitor
            ];
        const agentTypeEndpoint =
            CONSUMPTION_AGENT_RESULT_AGENT_TYPE_ENDPOINT[consumptionAgentTranslated.agentType];

        let params = new HttpParams();
        if (consumptionAgentTranslated.reportId != null) {
            params = params.append('reportid', consumptionAgentTranslated.reportId);
        }

        const url = `${this.baseUrl}/${buildingNo}/consumptionagents/${consumptionToMonitorEndpoint}/${agentTypeEndpoint}`;
        const body =
            consumptionAgentTranslated.reportId === null
                ? setupConsumptionAgentRequestResultBody(consumptionAgentTranslated)
                : {};
        return this.http
            .post(url, body, {
                withCredentials: true,
                params,
            })
            .pipe(mapParseItem(consumptionAgentResultSchema));
    }

    addTranslationsForAgent(): (
        source$: Observable<ConsumptionAgent>,
    ) => Observable<ConsumptionAgentTranslated> {
        return source$ =>
            source$.pipe(
                map(agent => [agent]),
                this.addTranslationsForAgents(),
                map(translatedAgents => translatedAgents[0]),
            );
    }

    addTranslationsForAgents(): (
        source$: Observable<ConsumptionAgent[]>,
    ) => Observable<ConsumptionAgentTranslated[]> {
        return (source$: Observable<ConsumptionAgent[]>) =>
            source$.pipe(
                map(agents =>
                    agents.map(agent => {
                        const usageAndUnitsTranslated = agent.meterSettings.usageAndUnits
                            .map(
                                ({alloUnitType, unit}) =>
                                    `${this.translateService.translate(`mappers_allocationUnitMap.${alloUnitType}`, 'online', 'common')} - ${this.translateService.translate(`mappers_measurementUnit.${unit}`, 'online', 'common')}`,
                            )
                            .join(', ');

                        return {
                            ...agent,
                            investigation: this.translateService.translate(
                                CONSUMPTION_AGENT_TYPE_INVESTIGATIONS.find(
                                    invest => invest.key === agent.agentType,
                                )?.labelKey ?? '',
                            ),
                            consumptionToMonitorTranslated: this.translateService.translate(
                                `consumption-agents_consumptionType_${agent.consumptionToMonitor}`,
                            ),
                            usageAndUnitsTranslated,
                        };
                    }),
                ),
            );
    }

    exportConsumptionAgentResults(
        buildingNo: string,
        consumptionAgent: ConsumptionAgentTranslated,
    ): Observable<ConsumptionAgentExport> {
        const consumptionToMonitorEndpoint =
            CONSUMPTION_AGENT_RESULT_MONITOR_ENDPOINT[consumptionAgent.consumptionToMonitor];
        const agentTypeEndpoint =
            CONSUMPTION_AGENT_RESULT_AGENT_TYPE_ENDPOINT[consumptionAgent.agentType];
        let params = new HttpParams().set('languagetag', this.translateService.language);
        if (consumptionAgent.reportId != null) {
            params = params.append('reportid', consumptionAgent.reportId);
        }
        const body =
            consumptionAgent.reportId === null
                ? setupConsumptionAgentRequestResultBody(consumptionAgent)
                : {};
        const url = `${this.baseUrl}/${buildingNo}/exports/consumptionagents/${consumptionToMonitorEndpoint}/${agentTypeEndpoint}`;

        return this.http
            .post(url, body, {
                withCredentials: true,
                observe: 'response',
                responseType: 'blob',
                params,
            })
            .pipe(
                map(response => response.body),
                filter(responseBody => responseBody != null),
                map(responseBody => ({
                    blob: new Blob([responseBody], {type: 'xlsx'}),
                    fileName: `${this.translateService.translate('consumption-agents_consumption-agent')} - ${consumptionAgent.agentName} - ${DateTime.now().toLocaleString()}.xlsx`,
                })),
            );
    }
}
